import css from './index.module.scss';
import { calcEnergyGeneration, getLocalData, getAsynItem, SolarItemType, ProductItemType, RecommandMap, BungleImgMap, formatNumber } from './data';
import { cloneDeep } from 'lodash';
import { useLang } from '@/i18n';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import GetAppIcon from './Mapping';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Scrollbar, Navigation } from 'swiper';
import EcoImg from 'eco-design/base/eco-img';
import EcoLink from 'eco-design/base/eco-link';
import gtm from '@/utils/gtm';

interface E_GSProps {
    callBuy: (item: any) => void;
    updateSolar: (item: any) => void;
}
export default function E_GS({ callBuy, updateSolar }: E_GSProps) {
    const { locale = '' } = useRouter();
    const { t } = useLang('vanliferoadtrips');
    const [gap, setGap] = useState<number>(20);
    const [open, setOpen] = useState<boolean>(false);
    const { solar_panels, products }: { solar_panels: SolarItemType[]; products: ProductItemType[] } = getLocalData(locale);
    const [solarPanels, setSolarPanels] = useState<SolarItemType[]>([]);
    const [productCards, setProductCards] = useState<ProductItemType[]>([]);
    const IS_ROW = ['au', 'za', 'kr'].includes(locale);
    const IS_UK = ['uk'].includes(locale);
    const IS_EU = ['eu', 'uk', 'de', 'fr', 'it', 'es'].includes(locale);
    const suffix = IS_UK ? 'uk' : IS_EU ? 'eu' : IS_ROW ? locale : 'en';

    const handleChangeChild = (item: ProductItemType, solar: SolarItemType) => {
        const newProductCards = cloneDeep(productCards);
        const product = newProductCards?.find((p: ProductItemType) => p.variantId === item.variantId);
        if (product) {
            product?.children?.forEach((child: SolarItemType) => (child.isActive = child.variantId === solar.variantId));
        }
        setProductCards(newProductCards);
    };

    const handleQuantityChange = (item: ProductItemType, delta: number) => {
        const newProductCards = cloneDeep(productCards);
        const product = newProductCards?.find((p: ProductItemType) => p.variantId === item.variantId);
        if (product) {
            const child = product?.children?.find((c: SolarItemType) => c.isActive);
            if (child) {
                const { inventoryPolicy, inventoryQuantity } = child;
                const [min, max] = [0, inventoryPolicy === 'continue' ? 100 : Math.min(100, inventoryQuantity)];
                if (child.quantity + delta >= min && child.quantity + delta <= max) {
                    child.quantity = Math.max(child.quantity + delta, 0);
                }
            }
        }
        setProductCards(newProductCards);
    };

    const handleBuy = (card: ProductItemType) => {
        const { productId, variantId, productTitle, skuTitle, variantImg, img, price, compareAtPrice, inventoryPolicy, inventoryQuantity, children } = card;
        if (inventoryPolicy !== 'continue' && inventoryQuantity <= 0) return;
        const title = productTitle || skuTitle || '';
        try {
            gtm.push({
                event: 'uaEvent',
                event_name: 'buy',
                product_name: title,
                position: `Outdoor Recreation Page_${title}`,
            });
            // fb埋点
            // @ts-ignore
            window?.fbq && window?.fbq('track', 'Search');
        } catch (e) {
            console.error(e);
        } finally {
            const [_, max] = [0, inventoryPolicy === 'continue' ? 100 : Math.min(100, inventoryQuantity)];
            const addCarts: any = [
                {
                    discountPrice: price,
                    originalPrice: compareAtPrice,
                    inventory: max,
                    productId,
                    variantId,
                    img: variantImg || img || '',
                    title,
                },
            ];
            const target = children?.find((item: any) => item?.isActive);
            if (target && target.quantity > 0) {
                const {
                    price,
                    compareAtPrice,
                    inventoryPolicy,
                    inventoryQuantity,
                    productId,
                    variantId,
                    variantImg,
                    img,
                    parentVariantId,
                    productTitle,
                    skuTitle,
                    quantity,
                } = target;
                const title = productTitle || skuTitle || '';
                const [_, max] = [0, inventoryPolicy === 'continue' ? 100 : Math.min(100, inventoryQuantity)];
                addCarts.push({
                    discountPrice: price,
                    originalPrice: compareAtPrice,
                    inventory: max,
                    productId: productId,
                    variantId: variantId,
                    img: variantImg || img || '',
                    title: `${title}${t(`secE_${card.id}_title`) ? `\n(${t(`secE_${card.id}_title`)})` : ''}`,
                    quantity,
                    parentVariantId,
                });
            }
            callBuy(addCarts);
        }
    };

    useEffect(() => {
        const resetGap = () => {
            const winW = window.innerWidth;
            if (winW >= 1440) {
                return setGap(24);
            } else if (winW > 1024) {
                return setGap(20);
            } else {
                return setGap(16);
            }
        };
        resetGap();
    }, []);

    useEffect(() => {
        const initialSolarPanel = async () => {
            const promises: Promise<SolarItemType>[] = [];
            solar_panels?.forEach((item: SolarItemType) => promises.push(getAsynItem(item, locale) as Promise<SolarItemType>));
            const inited = await Promise.all(promises);
            setSolarPanels(inited);
        };
        initialSolarPanel();
    }, []);

    useEffect(() => {
        const initialProductCards = async () => {
            const promises: Promise<ProductItemType>[] = [];
            products?.forEach((item: ProductItemType) => promises.push(getAsynItem(item, locale) as Promise<ProductItemType>));
            const inited = await Promise.all(promises);
            inited?.map((item: ProductItemType) => {
                item.quantity = 1;
                if (item?.childrenIds?.length > 0) {
                    item.children = [] as SolarItemType[];
                    item.childrenIds?.forEach((childId: string, k: number) => {
                        const child: SolarItemType | undefined = solarPanels?.find((sp: SolarItemType) => sp.id === childId);
                        if (child) {
                            const recommand = RecommandMap?.[item.id]?.[child.id] || 0;
                            item.children?.push({
                                ...child,
                                parentVariantId: item.variantId,
                                quantity: Math.max(Math.min(recommand[0], child.inventoryPolicy === 'continue' ? 100 : child.inventoryQuantity), 0),
                                recommand,
                                isActive: k === 0,
                            });
                        }
                    });
                }
            });
            setProductCards(inited);
        };
        solarPanels?.length > 0 && initialProductCards();
    }, [solarPanels]);

    useEffect(() => {
        const { variantId = '', parentVariantId = '', quantity = -1 } = updateSolar || {};
        const targetSP = solarPanels?.find((sp: any) => sp?.variantId === variantId);
        if (variantId && parentVariantId && quantity >= 0 && targetSP) {
            const tempArr = cloneDeep(productCards);
            const cardTargetIdx = tempArr?.findIndex((item: any) => item?.variantId === parentVariantId);
            if (cardTargetIdx !== -1) {
                const solarTargetIdx = tempArr[cardTargetIdx]?.children.findIndex((item: any) => item?.variantId === variantId);
                if (solarTargetIdx !== -1) {
                    tempArr[cardTargetIdx].children[solarTargetIdx]['quantity'] = quantity;
                }
            }
            setProductCards(tempArr);
        }
    }, [updateSolar, solarPanels]);

    return (
        <div id="outdoor-recreation-E" className={`outdoor-recreation-E outdoor-recreation-box inner ${css['outdoor-recreation-E']}`}>
            <h3 className="title">{t('secE_title')}</h3>
            <Swiper
                className="cards swiper-box"
                spaceBetween={gap}
                slidesPerView={'auto'}
                initialSlide={0}
                modules={[Scrollbar, Navigation]}
                navigation
                scrollbar={{
                    hide: false,
                    draggable: true,
                    dragSize: 32,
                    snapOnRelease: true,
                }}
            >
                {productCards?.map((item: ProductItemType, k: number) => {
                    const activeChild = item.children?.find((child: SolarItemType) => child.isActive) || item.children?.[0];
                    const { variantId, id, productTitle, skuTitle, descArr, icons, storage, children, link_more } = item;
                    const { quantity, recommand, energy, inventoryPolicy, inventoryQuantity } = activeChild;
                    const isSP =
                        item?.children?.length > 0 && item?.children?.every((child: SolarItemType) => !['campingLight', 'trailDCBag'].includes(child.id));
                    const recommandStatu = quantity === 0 ? 0 : recommand.includes(quantity) ? 1 : 2;
                    const [min, max] = [0, inventoryPolicy === 'continue' ? 100 : Math.min(100, inventoryQuantity)];
                    const isSoldOut = item?.inventoryPolicy !== 'continue' && item?.inventoryQuantity <= 0;
                    return (
                        <SwiperSlide className="card" key={`${variantId}-${id}`}>
                            <div className="card-info">
                                <div className="card-img">
                                    <EcoImg
                                        pc={
                                            BungleImgMap?.[id]?.[activeChild.id]?.[suffix]?.trim() ||
                                            BungleImgMap?.[id]?.[activeChild.id]?.['en']?.trim() ||
                                            BungleImgMap?.[id]?.[activeChild.id]?.['eu']?.trim() ||
                                            item.variantImg ||
                                            item.img ||
                                            ''
                                        }
                                    />
                                </div>
                                <p className="card-power">{t(`secE_${id}_power`)}</p>
                                <p className="card-title">{t(`secE_${id}_title`) || productTitle || skuTitle || ''}</p>
                                <ul className="card-descs">
                                    {descArr?.map((_: string, m: number) => (
                                        <li className="card-desc" key={`${k}-${m}`}>
                                            {t(`secE_${id}_desc${m + 1}`)}
                                        </li>
                                    ))}
                                </ul>
                                {open ? (
                                    <ul className="card-icons">
                                        {icons?.map((icon: string, m: number) => (
                                            <li className="card-icon" key={`${k}-${m}`}>
                                                <div className="icon-svg">
                                                    <GetAppIcon appliance={icon} />
                                                </div>
                                                <p className="icon-title">
                                                    {t.rich(`secE_${id}_icon${m + 1}_title`, {
                                                        s: (text: string) => text,
                                                    })}
                                                </p>
                                                <p className="icon-desc">{t(`secE_${id}_icon${m + 1}_desc`)}</p>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <span className="card-open" onClick={() => setOpen(true)}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="9" height="5" viewBox="0 0 9 5" fill="none">
                                            <path d="M1.5 1L4.5 4L7.5 1" stroke="#626262" strokeWidth="1.21739" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                )}
                                <div className="card-line"></div>
                                {isSP && (
                                    <>
                                        <p className="calc-title">{t('common_card_calculator')}</p>
                                        <p
                                            className={`calc-result ${
                                                recommandStatu === 0 ? 'recommand-0' : recommandStatu === 1 ? 'recommand-range' : 'not-recommand'
                                            }`}
                                        >
                                            {recommandStatu === 0
                                                ? t.rich('common_card_0', {
                                                      storagePower: formatNumber(storage, locale),
                                                      s: (text: string) => <span className="s">{text}</span>,
                                                  })
                                                : recommandStatu === 1
                                                ? t.rich('common_card_calculator_res', {
                                                      generatePower: formatNumber(calcEnergyGeneration(energy, quantity, locale), locale),
                                                      storagePower: formatNumber(storage, locale),
                                                      s: (text: string) => <span className="s">{text}</span>,
                                                  })
                                                : t('common_card_no_recommand')}
                                        </p>
                                    </>
                                )}
                                <p className="sp-label">{t(isSP ? 'common_card_sp_output' : 't200_common_card_sp_output')}</p>
                                <ul className="card-children">
                                    {children?.map((child: SolarItemType, m: number) => (
                                        <li
                                            className={`card-child${child.isActive ? ' active' : ''}`}
                                            key={`${variantId}-${id}-${child.variantId}-${m}`}
                                            onClick={() => handleChangeChild(item, child)}
                                        >
                                            {t(`secE_card_${child.id}`) || child.productTitle || child.skuTitle || child.id.toUpperCase() || ''}
                                        </li>
                                    ))}
                                </ul>
                                <p className="sp-label sp-quantity">{t(isSP ? 'common_card_sp_quantity' : 't200_common_card_sp_quantity')}</p>
                                <div className="card-children-quantity">
                                    <button className={`calc-operator${quantity <= min ? ' disabled' : ''}`} onClick={() => handleQuantityChange(item, -1)}>
                                        -
                                    </button>
                                    <span className="calc-operator calc-quantity">{quantity}</span>
                                    <button className={`calc-operator${quantity >= max ? ' disabled' : ''}`} onClick={() => handleQuantityChange(item, 1)}>
                                        +
                                    </button>
                                </div>
                            </div>
                            <div className="card-btns">
                                <button className={`card-btn btn-cart${isSoldOut ? ' disabled' : ''}`} onClick={() => handleBuy(item)}>
                                    {isSoldOut ? t('common_card_out') : t('common_card_buy')}
                                </button>
                                <EcoLink href={link_more} className="card-btn btn-more">
                                    {t('common_card_more')}
                                </EcoLink>
                            </div>
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        </div>
    );
}
